import { useCallback, useEffect, useState } from "react";

import { ago } from "../components/MatchCard";
import PairDevice from "../components/PairDevice";
import { api, setToken } from "../lib/api";
import type { Budget, Device, Health, StreamEvent } from "../lib/types";

/** Budget and health: the two numbers that say whether this is working. */
export default function System({ event }: { event: StreamEvent | null }) {
  const [budget, setBudget] = useState<Budget | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [b, h, d] = await Promise.all([api.budget(), api.health(), api.devices()]);
      setBudget(b);
      setHealth(h);
      setDevices(d);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (event && (event.type === "poll" || event.type === "budget_warning")) void load();
  }, [event, load]);

  const pct = budget ? Math.min(1, budget.pct) : 0;
  const meterClass = pct >= (budget?.alert_pct ?? 0.85) ? "risk" : pct >= 0.6 ? "warn" : "";

  return (
    <div className="stack">
      {error && <div className="error">{error}</div>}

      {budget && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ fontSize: 15 }}>eBay call budget - {budget.day}</h2>
            <span className="mono">
              {budget.used} / {budget.cap}
            </span>
          </div>
          <div className={`meter ${meterClass}`}>
            <span style={{ width: `${pct * 100}%` }} />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <span className="pill">{budget.remaining} remaining</span>
            <span className={`pill ${budget.projected_day_total > budget.cap ? "risk" : "pass"}`}>
              projected {budget.projected_day_total}
            </span>
            {budget.by_endpoint.map((row) => (
              <span className="pill" key={row.endpoint}>
                {row.endpoint}: {row.calls}
              </span>
            ))}
          </div>
          {budget.projected_day_total > budget.cap && (
            <p className="faint" style={{ color: "var(--risk)" }}>
              At the current intervals today would exceed the cap. Lengthen a lane's interval or
              pause one.
            </p>
          )}
        </div>
      )}

      {budget && (
        <div className="card stack">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ fontSize: 15 }}>Classifier spend today</h2>
            <span className="mono">${budget.model_spend_today_usd.toFixed(2)}</span>
          </div>
          {!budget.model_spend.length ? (
            <span className="faint">No classifications yet today.</span>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Calls</th>
                  <th>In</th>
                  <th>Cached</th>
                  <th>Out</th>
                  <th>USD</th>
                </tr>
              </thead>
              <tbody>
                {budget.model_spend.map((row) => (
                  <tr key={row.model}>
                    <td className="mono">{row.model}</td>
                    <td className="mono">{row.calls}</td>
                    <td className="mono faint">{row.input_tokens.toLocaleString()}</td>
                    <td className="mono faint">{row.cache_read_tokens.toLocaleString()}</td>
                    <td className="mono">{row.output_tokens.toLocaleString()}</td>
                    <td className="mono">${row.usd.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <span className="faint">
            Output tokens dominate this bill. Unlike the eBay cap this has no ceiling, so it is
            worth a glance whenever you widen a watch or clear an exclude list.
          </span>
        </div>
      )}

      {health && (
        <>
          <div className="stats">
            <Stat label="Listings / 24h" value={health.listings_24h} />
            <Stat label="Classified / 24h" value={health.verdicts_24h} />
            <Stat label="Matches / 24h" value={health.matches_24h} />
            <Stat
              label="Index lag (median)"
              value={
                health.index_lag_median_seconds === null
                  ? "--"
                  : formatSeconds(health.index_lag_median_seconds)
              }
              hint={
                health.index_lag_sample === 0
                  ? "no freshly-listed items measured yet"
                  : `p90 ${
                      health.index_lag_p90_seconds === null
                        ? "--"
                        : formatSeconds(health.index_lag_p90_seconds)
                    } - n=${health.index_lag_sample}`
              }
            />
          </div>

          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Lane</th>
                  <th>Last poll</th>
                  <th>Interval now</th>
                  <th>Listings 24h</th>
                </tr>
              </thead>
              <tbody>
                {health.lanes.map((lane) => (
                  <tr key={lane.id}>
                    <td>
                      {lane.name}{" "}
                      {!lane.enabled && <span className="pill warn">paused</span>}
                      {lane.idle_unwatched && (
                        <span className="pill warn">idle - no watch</span>
                      )}
                      {lane.enabled &&
                        !lane.idle_unwatched &&
                        lane.seconds_since_poll !== null &&
                        lane.seconds_since_poll > lane.interval_now * 3 && (
                          <span className="pill risk">stalled</span>
                        )}
                    </td>
                    <td className="faint">{ago(lane.last_polled_at)}</td>
                    <td className="mono faint">{lane.interval_now}s</td>
                    <td className="mono">{lane.listings_24h}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card row" style={{ gap: 8 }}>
            <span className="pill">{health.daytime ? "daytime rates" : "overnight rates"}</span>
            <span className="pill mono">{health.local_tz}</span>
            <span className="pill mono">{health.classifier_model}</span>
            <span className="pill">notifier: {health.notifier}</span>
            <span className="pill">{health.sse_subscribers} live client(s)</span>
          </div>
        </>
      )}

      <div className="card stack">
        <h2 style={{ fontSize: 15 }}>Devices</h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Platform</th>
              <th>Last seen</th>
              <th>Push token</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr key={device.id}>
                <td>{device.name}</td>
                <td className="faint">{device.platform}</td>
                <td className="faint">{ago(device.last_seen_at)}</td>
                <td className="faint">{device.fcm_registered ? "registered" : "--"}</td>
                <td>
                  <button
                    className="ghost danger"
                    onClick={async () => {
                      if (!confirm(`Revoke "${device.name}"?`)) return;
                      try {
                        await api.revokeDevice(device.id);
                        void load();
                      } catch (err) {
                        setError(err instanceof Error ? err.message : String(err));
                      }
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ gap: 8 }}>
          <button onClick={() => setPairing((open) => !open)}>
            {pairing ? "Close" : "Pair a phone"}
          </button>
          <button
            className="ghost"
            onClick={() => {
              setToken(null);
              location.reload();
            }}
          >
            Forget this device's token in this browser
          </button>
        </div>
        {/* Mounted only while open: unmounting is what revokes an unclaimed code. */}
        {pairing && (
          <div className="card" style={{ marginTop: 4 }}>
            <PairDevice onPaired={() => void load()} />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="card">
      <div className="faint">{label}</div>
      <div className="stat">{value}</div>
      {hint && <div className="faint">{hint}</div>}
    </div>
  );
}

function formatSeconds(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)}s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}
